import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import { deserialize } from 'ts-jackson'
import { ESignatureDocument, ESignatureTemplate, MembershipESignatureDocument } from './model'

export const ESignatureService = (client: AxiosInstance) => {
  return {
    async getESignatureTemplateByKey(key: string): Promise<ESignatureTemplate> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>>(`/e-signature/templates/${key}`)
      )
      return deserialize(data, ESignatureTemplate)
    },
    async getSignedPdf(pdfBlob: Blob, fieldSigners?: string, eSignatureId?: string): Promise<Blob> {
      const formData = new FormData()
      formData.append('pdf', pdfBlob, 'unsigned-document.pdf')
      if (typeof fieldSigners === 'string') {
        formData.append('fieldSigners', fieldSigners)
      }

      const hasValidId = typeof eSignatureId === 'string' && eSignatureId.trim().length > 0
      const endpoint = hasValidId
        ? `/e-signature/sign/${encodeURIComponent(eSignatureId)}`
        : '/e-signature/sign'

      const { data } = await wrapApiCall(() =>
        client.post<Blob>(endpoint, formData, {
          responseType: 'blob',
          headers: {
            Accept: 'application/pdf',
            'Content-Type': 'multipart/form-data',
          },
        })
      )

      return data
    },
    async getTemplatePdf(key: string): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<unknown>(`/e-signature/templates/${key}/file`, {
          responseType: 'blob',
          headers: { Accept: 'application/pdf' },
        })
      )

      if (data instanceof Blob) {
        return data
      }

      if (data instanceof ArrayBuffer) {
        return new Blob([data], { type: 'application/pdf' })
      }

      if (typeof data === 'string') {
        return new Blob([data], { type: 'application/pdf' })
      }

      console.warn('[E-Signature] Unexpected template PDF payload', data)
      return new Blob([], { type: 'application/pdf' })
    },
    async getDocumentVersionFile(documentVersionId: string): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<unknown>(
          `/e-signature/document-version/${encodeURIComponent(documentVersionId)}/file`,
          {
            responseType: 'blob',
            headers: { Accept: 'application/pdf' },
          }
        )
      )

      if (data instanceof Blob) {
        return data
      }

      if (data instanceof ArrayBuffer) {
        return new Blob([data], { type: 'application/pdf' })
      }

      if (typeof data === 'string') {
        return new Blob([data], { type: 'application/pdf' })
      }

      console.warn('[E-Signature] Unexpected document-version PDF payload', data)
      return new Blob([], { type: 'application/pdf' })
    },
    async getUserDocuments(userId: number): Promise<ESignatureDocument[]> {
      const { data } = await wrapApiCall(() =>
        client.get<Record<string, unknown>[]>(`/e-signature/user/${userId}`)
      )
      return (
        data?.map((item: Record<string, unknown>) => deserialize(item, ESignatureDocument)) ?? []
      )
    },
    async getMembershipDocuments(membershipIds: number[]): Promise<MembershipESignatureDocument[]> {
      if (!Array.isArray(membershipIds) || membershipIds.length === 0) {
        return []
      }
      const params = {
        membershipIds: membershipIds.join(','),
      }
      const { data } = await wrapApiCall(() =>
        client.get<MembershipESignatureDocument[]>('/e-signature/membership', { params })
      )

      return data
    },
    async downloadPdf(documentId: string | number): Promise<Blob> {
      const { data } = await wrapApiCall(() =>
        client.get<unknown>(`/e-signature/${documentId}/pdf`, {
          responseType: 'blob',
          headers: { Accept: 'application/pdf' },
        })
      )

      if (data instanceof Blob) {
        return data
      }

      if (data instanceof ArrayBuffer) {
        return new Blob([data], { type: 'application/pdf' })
      }

      if (typeof data === 'string') {
        return new Blob([data], { type: 'application/pdf' })
      }

      console.warn('[E-Signature] Unexpected document PDF payload', data)
      return new Blob([], { type: 'application/pdf' })
    },
  }
}
